const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4200/api';


/** Activity report request.status: 1 = Complete/Submitted, 2 = In Progress */
export const ACTIVITY_REQUEST_STATUS = {
  OVERDUE: 0,
  TERMINAL_SUCCESS: 1,
  IN_PROGRESS: 2,
  NEW_REQUEST: 3,
  DRAFT: 4,
  SCHEDULED: 5,
};

/** @param {number} status @param {string} [role] */
export function mapActivityRequestStatusCodeToLabel(status, role = 'operations') {
  if (status === ACTIVITY_REQUEST_STATUS.OVERDUE) return 'Overdue';
  if (status === ACTIVITY_REQUEST_STATUS.TERMINAL_SUCCESS) {
    return role === 'partner' ? 'Submitted' : 'Complete';
  }
  if (status === ACTIVITY_REQUEST_STATUS.IN_PROGRESS) return 'In Progress';
  if (status === ACTIVITY_REQUEST_STATUS.NEW_REQUEST) return 'New Request';
  if (status === ACTIVITY_REQUEST_STATUS.DRAFT) return 'Draft';
  if (status === ACTIVITY_REQUEST_STATUS.SCHEDULED) return 'Scheduled';
  return 'Unknown Status';
}

export function isPartnerActivityReportReadOnly(role, statusCode, statusTypeLabel) {
  if (role !== 'partner') return false;
  if (statusTypeLabel === 'Submitted') return true;
  if (typeof statusCode === 'number' && statusCode === ACTIVITY_REQUEST_STATUS.TERMINAL_SUCCESS) {
    return true;
  }
  return false;
}

/**
 * Fetch companies data from the backend API
 * @returns {Promise<Object>} Companies data response
 */
export const fetchCompaniesData = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/activity-reports/companies`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        // Add authentication headers if needed
        // 'Authorization': `Bearer ${getAuthToken()}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching companies data:', error);
    throw error;
  }
};

/**
 * Create a new report item
 * @param {Object} reportData - Report item data
 * @returns {Promise<Object>} Created report item
 */
export const createReportItem = async (reportData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/activity-reports/requests`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(reportData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating report item:', error);
    throw error;
  }
};

/** 
 * Fetch all reports data from the backend API
 * @returns {Promise<Object>} All reports data response
 */
export const fetchAllReportsData = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/activity-reports/requests`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log("fetchAllReportsData - data: ", data);
    return data;
  } catch (error) {
    console.error('Error fetching all reports data:', error);
    throw error;
  }
};

/** 
 * Fetch reports data filtered by partner from the backend API
 * @param {string} partnerName - The name of the partner
 * @param {string} partnerDocName - The document name of the partner
 * @returns {Promise<Object>} Reports data response filtered by partner
 */
export const fetchReportsByPartner = async (partnerName, partnerDocName) => {
  try {
    // Encode the parameters to handle special characters like spaces
    const encodedPartnerName = encodeURIComponent(partnerName);
    const encodedPartnerDocName = encodeURIComponent(partnerDocName);
    
    // Correct URL format: use & to separate parameters, no quotes around values
    const url = `${API_BASE_URL}/activity-reports/requests-by-partner?partner_name=${encodedPartnerName}&partner_doc_name=${encodedPartnerDocName}`;
    console.log("Fetching reports by partner from URL:", url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log("fetchReportsByPartner - data: ", data);
    return data;
  } catch (error) {
    console.error('Error fetching reports by partner:', error);
    throw error;
  }
};

/** 
 * Fetch activity records by request ID, partner name, and partner document name
 * @param {string} requestId - The ID of the request
 * @param {string} partnerName - The name of the partner
 * @param {string} partnerDocName - The document name of the partner
 * @returns {Promise<Object>} Activity records response
 */
export const fetchActivityRecords = async (requestId, partnerName, partnerDocName) => {
  try {
    // Encode the parameters to handle special characters like spaces
    const encodedPartnerName = encodeURIComponent(partnerName);
    const encodedPartnerDocName = encodeURIComponent(partnerDocName);
    
    const url = `${API_BASE_URL}/activity-reports/records?request_id=${requestId}&partner_name=${encodedPartnerName}&partner_doc_name=${encodedPartnerDocName}`;
    
    console.log("Fetching activity records from URL:", url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log("fetchActivityRecords - data: ", data);
    return data;
  } catch (error) {
    console.error('Error fetching activity records:', error);
    throw error;
  }
}; 

/**
 * Fetch companies saved for a specific request
 * @param {string} requestId - The request ID
 * @returns {Promise<Object>} Companies for that request
 */
export const fetchCompaniesByRequestId = async (requestId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/activity-reports/requests/${requestId}/companies`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching companies by request ID:', error);
    throw error;
  }
};

/**
 * Fetch a single activity report request (includes updated_at, status, etc.)
 * @param {string} requestId
 * @returns {Promise<Object>} Parsed JSON body (typically { status, data })
 */
export const fetchActivityReportRequestById = async (requestId) => {
  const response = await fetch(
    `${API_BASE_URL}/activity-reports/requests/${requestId}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
  );
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const updateReportItem = async (requestId, reportData) => {
  const response = await fetch(
    `${API_BASE_URL}/activity-reports/requests/${requestId}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: requestId, ...reportData }),
    }
  );
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
};